/**
 * Pure builder for the copy-pasteable curl example shown on the "Capture a
 * test submission" panel (editor step 2, field-mapping.md §3.2 and §7). Uses
 * the campaign's current source fields (key -> sample) as the example body
 * when any are known, otherwise the standard key set recommended to landing
 * page developers so the example is still useful before any field exists.
 */

import type { SourceField } from '@/api/campaigns/types';

const STANDARD_SAMPLE_PAYLOAD: Record<string, string> = {
    name: 'Jane Doe',
    email: 'jane@example.com',
    phone: '+15551234567',
};

/** Turns a source field's sample into a JSON-safe value, falling back to an empty string. */
function sampleValue(field: SourceField): unknown {
    if (field.sample === undefined || field.sample === null) return '';
    return field.sample;
}

export function buildCurlExamplePayload(sourceFields: SourceField[] = []): Record<string, unknown> {
    if (sourceFields.length === 0) {
        return { ...STANDARD_SAMPLE_PAYLOAD };
    }
    return sourceFields.reduce<Record<string, unknown>>((payload, field) => {
        payload[field.key] = sampleValue(field);
        return payload;
    }, {});
}

export function buildCurlExample(endpointUrl: string, sourceFields: SourceField[] = []): string {
    const payload = buildCurlExamplePayload(sourceFields);
    const body = JSON.stringify(payload, null, 2);

    return [
        `curl -X POST '${endpointUrl}' \\`,
        `  -H 'Content-Type: application/json' \\`,
        `  -d '${body}'`,
    ].join('\n');
}
